import React from 'react';
import { useIsMounted } from 'utils';
import { useSiteLayoutClient } from './SiteLayoutClientProvider';
import type { SiteLayoutClient } from './siteLayoutClient';
import { SiteLayoutError, type SiteLayoutState } from './types';

type ClientMethod<K extends keyof SiteLayoutClient> =
  SiteLayoutClient[K] extends (
    siteId: string,
    ...rest: infer Rest
  ) => infer Result
    ? (...rest: Rest) => Result
    : never;

export type SiteLayoutActions = {
  [K in Exclude<keyof SiteLayoutClient, 'getState'>]: ClientMethod<K>;
};

export type UseSiteLayoutResult = {
  state: SiteLayoutState | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  actions: SiteLayoutActions;
  /** A command was given up on; nothing more can change until the page is reloaded. */
  stalled: boolean;
};

const ACTION_NAMES: Array<Exclude<keyof SiteLayoutClient, 'getState'>> = [
  'openDraft',
  'discardDraft',
  'publishDraft',
  'addZone',
  'renameZone',
  'removeZone',
  'addEntry',
  'renameEntry',
  'updateEntrySide',
  'removeEntry',
  'assignReader',
  'unassignReader',
];

/**
 * Loads a site's layout state through the `SiteLayoutClient` and exposes
 * every command that changes it, each bound to the site and followed by a
 * refetch of the state, whether it succeeded or not.
 */
export const useSiteLayout = (siteId: string): UseSiteLayoutResult => {
  const client = useSiteLayoutClient();
  const isMounted = useIsMounted();
  const [state, setState] = React.useState<SiteLayoutState | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [stalled, setStalled] = React.useState(false);
  // Only the latest read of the current site may set the state: a slower,
  // older read is dropped, and so is a read a command on the previous site
  // started after the site changed.
  const latestRead = React.useRef(0);
  const currentSiteId = React.useRef(siteId);
  currentSiteId.current = siteId;

  const refetch = React.useCallback(async (): Promise<void> => {
    latestRead.current += 1;
    const read = latestRead.current;
    const isCurrent = (): boolean =>
      isMounted() &&
      read === latestRead.current &&
      siteId === currentSiteId.current;
    setLoading(true);
    try {
      const next = await client.getState(siteId);
      if (isCurrent()) {
        setState(next);
        setError(null);
      }
    } catch (loadError) {
      if (isCurrent()) setError(loadError as Error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [client, siteId, isMounted]);

  // A site's layout is never shown, or acted on, under another site.
  React.useEffect(() => {
    setState(null);
    setError(null);
    refetch();
  }, [refetch]);

  const actions = React.useMemo((): SiteLayoutActions => {
    const bound = {} as Record<
      string,
      (...rest: unknown[]) => Promise<unknown>
    >;
    ACTION_NAMES.forEach((name) => {
      bound[name] = async (...rest: unknown[]): Promise<unknown> => {
        const method = client[name] as (...args: unknown[]) => Promise<unknown>;
        // A failed command may have changed the server part way (placing a
        // reader is three mutations), or failed because someone else changed
        // the layout: the state is read again either way.
        try {
          return await method.call(client, siteId, ...rest);
        } catch (commandError) {
          if (
            commandError instanceof SiteLayoutError &&
            commandError.code === 'STALLED' &&
            isMounted()
          )
            setStalled(true);
          throw commandError;
        } finally {
          await refetch();
        }
      };
    });
    return bound as unknown as SiteLayoutActions;
  }, [client, siteId, refetch, isMounted]);

  return { state, loading, error, refetch, actions, stalled };
};
